import { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import Home from "./components/pages/Home";
import History from "./components/pages/History";
import Header from "./components/layout/Header";
import Mayor from "./components/pages/Mayor";
import DeputyMayor from "./components/pages/DeputyMayor";
import MunicipalSecretariats from "./components/pages/MunicipalSecretariats";
import SecretariatDetail from "./components/pages/SecretariatDetail";
import ExecutivePage from "./components/pages/ExecutivePage";
import News from "./components/pages/News";
import CurrentNews from "./components/ui_elements/news/CurrentNews";
import TransparencyPortal from "./components/pages/TransparencyPortal";
import TransparencyPortalPreviousManagement from "./components/pages/TransparencyPortalPreviousManagement";
import Documents from "./components/ui_elements/transparency_portal/Documents";
import TransparencyPortalInternalLink from "./components/pages/TransparencyPortalInternalLink";
import CommunicationChannels from "./components/pages/CommunicationChannels";
import Ouvidoria from "./components/pages/Ouvidoria";
import SIC from "./components/pages/SIC";
import ESIC from "./components/pages/ESIC";
import OrganizationalStructure from "./components/pages/OrganizationalStructure";
import FAQ from "./components/pages/FAQ";
import SocialMedia from "./components/pages/SocialMedia";
import ServiceCatalog from "./components/pages/ServiceCatalog";
import Accessibility from "./components/pages/accessibility/Accessibility";
import HighContrast from "./components/pages/accessibility/HighContrast";
import TextResize from "./components/pages/accessibility/TextResize";
import SiteMap from "./components/pages/accessibility/SiteMap";
import Breadcrumb from "./components/ui_elements/Breadcrumb";
import { BreadcrumbProvider } from "./context/BreadcrumbContext";
import Declaracao from "./components/pages/Declaracao";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import AccessibilitySidebar from "./components/ui_elements/AccessibilitySidebar";

const App = () => {
  const location = useLocation();
  const [newsData, setNewsData] = useState([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch("/assets/json/news.json");
        const data = await response.json();
        if (data.news && Array.isArray(data.news)) {
          setNewsData(data.news);
        } else {
          console.error("Estrutura inválida no JSON de notícias.");
        }
      } catch (error) {
        console.error("Erro ao carregar o JSON de notícias:", error);
      }
    };

    fetchNews();
  }, []);

  const getPageTitle = () => {
    if (location.pathname.startsWith("/noticias/")) {
      const newsId = location.pathname.split("/")[2];
      const currentNews = newsData.find((news) => news.ID === parseInt(newsId));
      return currentNews ? `${currentNews.category.name}` : "Sem categoria";
    }

    if (location.pathname.startsWith("/transparencia/documentos/")) {
      return decodeURIComponent(location.pathname.split("/")[4]);
    }

    if (location.pathname.startsWith("/secretarias/")) {
      return "Secretarias Municipais";
    }

    switch (location.pathname) {
      case "/":
        return "Bem-vindo ao Portal da Prefeitura de Serrinha";
      case "/historia":
        return "História de Serrinha";
      case "/governo":
        return "Gestão Municipal";
      case "/prefeito":
        return "Prefeito";
      case "/vice-prefeito":
        return "Vice-prefeito";
      case "/secretarias":
        return "Secretarias Municipais";

      case "/noticias":
        return "Notícias";
      case "/transparencia/estrutura-organizacional":
        return "Estrutura Organizacional";
      case "/transparencia/canais-de-comunicacao":
        return "Canais de Comunicação";
      case "/transparencia/ouvidoria":
        return "Ouvidoria";
      case "/transparencia/sic":
        return "SIC — Serviço de Informação ao Cidadão";
      case "/transparencia/esic":
        return "e-SIC — Solicitação Eletrônica";
      case "/transparencia/carta-de-servicos":
        return "Carta de Serviços";
      case "/transparencia/acessibilidade":
        return "Acessibilidade";
      case "/transparencia/acessibilidade/alto-contraste":
        return "Alto Contraste";
      case "/transparencia/acessibilidade/redimensionamento-de-texto":
        return "Redimensionamento de Texto";
      case "/transparencia/acessibilidade/mapa-do-site":
        return "Mapa do Site";
      case "/transparencia/perguntas-e-respostas":
        return "Perguntas e Respostas";
      case "/transparencia/redes-sociais":
        return "Redes Sociais";
      case "/transparencia/declaracao":
        return "Declaração";
      case "/transparencia":
        return "Portal da Transparência";
      case "/transparencia/anterior":
        return "Portal da Transparência - Gestão Anterior";
      default:
        if (location.pathname.startsWith("/transparencia/")) {
          return "Portal da Transparência";
        }
        return "Prefeitura de Serrinha";
    }
  };

  useEffect(() => {
    document.title = `Prefeitura de Serrinha - ${getPageTitle()}`;
  }, [location.pathname, newsData]);

  return (
    <AccessibilityProvider>
    <BreadcrumbProvider>
    <div className="App">
      <Navbar />
      <AccessibilitySidebar />
      <Header title={getPageTitle()} />
      <div className="content-wrapper">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/historia" element={<History />} />
              <Route path="/governo" element={<ExecutivePage />} />
              <Route path="/prefeito" element={<Mayor />} />
              <Route path="/vice-prefeito" element={<DeputyMayor />} />
              <Route
                path="/secretarias"
                element={<MunicipalSecretariats />}
              />
              <Route path="/secretarias/:id" element={<SecretariatDetail />} />
              <Route path="/noticias" element={<News />} />
              <Route path="/noticias/:id" element={<CurrentNews />} />
              <Route path="/transparencia/estrutura-organizacional" element={<OrganizationalStructure />} />
              <Route path="/transparencia/perguntas-e-respostas" element={<FAQ />} />
              <Route path="/transparencia/redes-sociais" element={<SocialMedia />} />
              <Route path="/transparencia/canais-de-comunicacao" element={<CommunicationChannels />} />
              <Route path="/transparencia/ouvidoria" element={<Ouvidoria />} />
              <Route path="/transparencia/sic" element={<SIC />} />
              <Route path="/transparencia/esic" element={<ESIC />} />
              <Route path="/transparencia/carta-de-servicos" element={<ServiceCatalog />} />
              <Route
                path="/transparencia"
                element={<TransparencyPortal />}
              />
              <Route
                path="/transparencia/anterior"
                element={<TransparencyPortalPreviousManagement />}
              />
              <Route path="/transparencia/documentos/:sectionId/:sectionItemTitle" element={<Documents />} />
              <Route path="/transparencia/declaracao" element={<Declaracao />} />
              <Route path="/transparencia/*" element={<TransparencyPortalInternalLink />} />
              <Route path="/transparencia/acessibilidade" element={<Accessibility />} />
              <Route path="/transparencia/acessibilidade/alto-contraste" element={<HighContrast />} />
              <Route path="/transparencia/acessibilidade/redimensionamento-de-texto" element={<TextResize />} />
              <Route path="/transparencia/acessibilidade/mapa-do-site" element={<SiteMap />} />
            </Routes>
          </Suspense>
        </div>
      </div>
      <Footer />
    </div>
    </BreadcrumbProvider>
    </AccessibilityProvider>
  );
};

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}
